import { Injectable } from '@angular/core';
import {combineLatestWith, Observable} from 'rxjs';
import { map } from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {AttributesStoreService} from '@emm//menu/store/attributes/attributes-store.service';
import {asyncActionToBoolean} from '@emm/core/util/ngrx.util';
import {MenuBaseStoreService} from '@emm/menu/store/base/menu-base-store.service';
import {MenuChangeSetsStoreService} from '@emm/menu/store/change-sets/menu-change-sets-store.service';
import {SubmenusStoreService} from '@emm/menu/store/submenus/submenus-store.service';
import {LocationSetsStoreService} from '@emm/menu/store/location-sets/location-sets-store.service';
import {LocationSetTypesStoreService} from '@emm/menu/store/location-set-types/location-set-types-store.service';
import {ItemsStoreService} from '@emm/menu/store/items/items-store.service';
import {ItemDetailStoreService} from '@emm/menu/store/item-detail/item-detail-store.service';
import {ItemChildrenStoreService} from '@emm/menu/store/item-children/item-children-store.service';
import {FeatureScopeStoreService} from '@emm/menu/store/feature-scope/feature-scope-store.service';
import {ConsumerTypesStoreService} from '@emm/menu/store/consumer-types/consumer-types-store.service';
import {ConsumersStoreService} from '@emm/menu/store/consumer/consumers-store.service';
import {ItemDetailAction} from '@emm/menu/store/item-detail/item-detail.actions';
import {ItemsAction} from '@emm/menu/store/items/items.actions';
import {ItemChildrenAction} from '@emm/menu/store/item-children/item-children.actions';
import {SubmenusAction} from '@emm/menu/store/submenus/submenus.actions';

@Injectable({
    providedIn: 'root'
})
export class MenuStoreService {

    queryAttributes = this.attributesStore.queries;
    queryBase = this.baseStore.queries;
    queryChangeSets = this.changeSetsStore.queries;
    queryConsumers = this.consumersStoreService.queries;
    queryConsumerTypes = this.consumerTypesStoreService.queries;
    queryFeatureScope = this.featureScopeStore.queries;
    queryItemChildren = this.itemChildrenStore.queries;
    queryItemDetail = this.itemDetailStore.queries;
    queryItems = this.itemsStore.queries;
    queryLocationSetTypes = this.locationSetTypesStore.queries;
    queryLocationSets = this.locationSetsStore.queries;
    querySubmenus = this.submenusStore.queries;

    dispatch = {
        ...this.attributesStore.dispatch,
        ...this.baseStore.dispatch,
        ...this.changeSetsStore.dispatch,
        ...this.consumersStoreService.dispatch,
        ...this.consumerTypesStoreService.dispatch,
        ...this.featureScopeStore.dispatch,
        ...this.itemChildrenStore.dispatch,
        ...this.itemDetailStore.dispatch,
        ...this.itemsStore.dispatch,
        ...this.locationSetTypesStore.dispatch,
        ...this.locationSetsStore.dispatch,
        ...this.submenusStore.dispatch,
    };

    isLoading$ = {
        attributes: this.queryAttributes.isLoading$,
        changeSets: this.queryChangeSets.isLoading$,
        consumers: this.queryConsumers.isLoading$,
        consumerTypes: this.queryConsumerTypes.isLoading$,
        itemChildren: this.queryItemChildren.isLoading$,
        itemDetail: this.queryItemDetail.isLoading$,
        items: this.queryItems.isLoading$,
        locationSetTypes: this.queryLocationSetTypes.isLoading$,
        locationSets: this.queryLocationSets.isLoading$,
        submenus: this.querySubmenus.isLoading$,
    };

    anyLoading$ = this.isLoading$.submenus.pipe(combineLatestWith([
            this.isLoading$.attributes,
            this.isLoading$.changeSets,
            this.isLoading$.consumers,
            this.isLoading$.consumerTypes,
            this.isLoading$.itemChildren,
            this.isLoading$.itemDetail,
            this.isLoading$.items,
            this.isLoading$.locationSetTypes,
            this.isLoading$.locationSets,
        ]),
        map((sources: [boolean, Observable<boolean>]) => R.any(R.equals(true), sources))
    );

    loadItems$ = asyncActionToBoolean(
        this.queryItems,
        ItemsAction.LoadSuccess,
        ItemsAction.LoadFailure
    );

    loadItemChildren$ = asyncActionToBoolean(
        this.queryItemChildren,
        ItemChildrenAction.LoadSuccess,
        ItemChildrenAction.LoadFailure
    );

    updateMenuItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.UpdateMenuItemSuccess,
        ItemDetailAction.UpdateMenuItemFailure
    );

    addItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.AddSuccess,
        ItemDetailAction.AddFailure
    );

    addSubmenu$ = asyncActionToBoolean(
        this.querySubmenus,
        SubmenusAction.AddSuccess,
        SubmenusAction.AddFailure
    );

    toggleItemDisabled$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.ToggleDisabledSuccess,
        ItemDetailAction.ToggleDisabledFailure
    );

    toggleItemDisabledMenuWide$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.ToggleDisabledMenuWideSuccess,
        ItemDetailAction.ToggleDisabledMenuWideFailure
    );

    deleteItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.DeleteSuccess,
        ItemDetailAction.DeleteFailure
    );

    copyItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.CopySuccess,
        ItemDetailAction.CopyFailure
    );

    pasteItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.PasteSuccess,
        ItemDetailAction.PasteFailure
    );

    moveItem$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.MoveSuccess,
        ItemDetailAction.MoveFailure
    );

    pasteCascade$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.PasteCascadeSuccess,
        ItemDetailAction.PasteCascadeFailure
    );

    deleteCascade$ = asyncActionToBoolean(
        this.queryItemDetail,
        ItemDetailAction.DeleteCascadeSuccess,
        ItemDetailAction.DeleteCascadeFailure
    );

    constructor(
        private attributesStore: AttributesStoreService,
        private baseStore: MenuBaseStoreService,
        private changeSetsStore: MenuChangeSetsStoreService,
        private consumersStoreService: ConsumersStoreService,
        private consumerTypesStoreService: ConsumerTypesStoreService,
        private featureScopeStore: FeatureScopeStoreService,
        private itemChildrenStore: ItemChildrenStoreService,
        private itemDetailStore: ItemDetailStoreService,
        private itemsStore: ItemsStoreService,
        private locationSetTypesStore: LocationSetTypesStoreService,
        private locationSetsStore: LocationSetsStoreService,
        private submenusStore: SubmenusStoreService
    ) {}

    selectItemChildren(path: string): any {
        return this.itemChildrenStore.selectItemChildren(path);
    }

}
